/**
 * Paginated List Component
 * Handles client-side pagination for section blocks with URL state management
 */

class PaginatedList {
  constructor(navElement) {
    this.nav = navElement;
    this.section = navElement.closest('.paginated-list');
    this.itemsContainer = this.section.querySelector('.paginated-list__items');
    this.items = Array.from(this.section.querySelectorAll('[data-page-item]'));
    this.pageNumbersContainer = this.nav.querySelector('[data-page-numbers]');
    this.prevButton = this.nav.querySelector('[data-page-nav="prev"]');
    this.nextButton = this.nav.querySelector('[data-page-nav="next"]');

    this.itemsPerPage = parseInt(this.nav.dataset.itemsPerPage, 10);
    this.totalItems = parseInt(this.nav.dataset.totalItems, 10);
    this.totalPages = parseInt(this.nav.dataset.totalPages, 10);
    this.currentPage = this.getPageFromURL();

    this.init();
  }

  init() {
    this.renderPageNumbers();
    this.showPage(this.currentPage);
    this.attachEventListeners();
  }

  /**
   * Get current page number from URL parameter
   * @returns {number} Page number (1-indexed), clamped to valid range
   */
  getPageFromURL() {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page'), 10);

    // Validate and clamp to valid range
    if (isNaN(page) || page < 1) {
      return 1;
    }
    if (page > this.totalPages) {
      return this.totalPages;
    }
    return page;
  }

  /**
   * Show items for the specified page
   * @param {number} pageNumber - Page to display (1-indexed)
   */
  showPage(pageNumber) {
    // Ensure page is in valid range
    pageNumber = Math.max(1, Math.min(pageNumber, this.totalPages));
    this.currentPage = pageNumber;

    // Calculate item range for current page
    const startIndex = (pageNumber - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    // Show/hide items based on current page
    this.items.forEach((item, index) => {
      if (index >= startIndex && index < endIndex) {
        item.style.display = '';
        item.removeAttribute('hidden');
      } else {
        item.style.display = 'none';
        item.setAttribute('hidden', '');
      }
    });

    // Update navigation state
    this.updateNavigationState();

    // Update URL
    this.updateURL(pageNumber);
  }

  /**
   * Update prev/next buttons and active page highlight
   */
  updateNavigationState() {
    // Update prev button
    if (this.currentPage === 1) {
      this.prevButton.setAttribute('aria-disabled', 'true');
      this.prevButton.style.pointerEvents = 'none';
      this.prevButton.style.opacity = '0.5';
    } else {
      this.prevButton.removeAttribute('aria-disabled');
      this.prevButton.style.pointerEvents = '';
      this.prevButton.style.opacity = '';
    }

    // Update next button
    if (this.currentPage === this.totalPages) {
      this.nextButton.setAttribute('aria-disabled', 'true');
      this.nextButton.style.pointerEvents = 'none';
      this.nextButton.style.opacity = '0.5';
    } else {
      this.nextButton.removeAttribute('aria-disabled');
      this.nextButton.style.pointerEvents = '';
      this.nextButton.style.opacity = '';
    }

    // Update active page highlight
    const pageLinks = this.pageNumbersContainer.querySelectorAll('[data-page]');
    pageLinks.forEach(link => {
      const linkPage = parseInt(link.dataset.page, 10);
      if (linkPage === this.currentPage) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      } else {
        link.classList.remove('is-active');
        link.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Render page number links (max 7 visible for UX)
   * Shows: [1] ... [4] [5] [6] ... [10] (centered around current page)
   */
  renderPageNumbers() {
    const maxVisible = 7;
    const pages = [];

    if (this.totalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let start = Math.max(2, this.currentPage - 1);
      let end = Math.min(this.totalPages - 1, this.currentPage + 1);

      // Adjust range if near boundaries
      if (this.currentPage <= 3) {
        end = Math.min(this.totalPages - 1, 5);
      } else if (this.currentPage >= this.totalPages - 2) {
        start = Math.max(2, this.totalPages - 4);
      }

      // Add ellipsis if gap at start
      if (start > 2) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if gap at end
      if (end < this.totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      if (this.totalPages > 1) {
        pages.push(this.totalPages);
      }
    }

    // Render page links
    this.pageNumbersContainer.innerHTML = pages.map(page => {
      if (page === '...') {
        return '<span class="pagination__ellipsis">…</span>';
      }
      return `<a href="#" class="pagination__link" data-page="${page}" aria-label="Page ${page}">${page}</a>`;
    }).join('');
  }

  /**
   * Update URL with current page parameter
   * @param {number} pageNumber - Page number to set in URL
   */
  updateURL(pageNumber) {
    const url = new URL(window.location);

    if (pageNumber === 1) {
      // Remove page parameter for first page (cleaner URL)
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', pageNumber);
    }

    // Update URL without page reload
    window.history.pushState({ page: pageNumber }, '', url);
  }

  /**
   * Attach event listeners for navigation
   */
  attachEventListeners() {
    // Prev button
    this.prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.currentPage > 1) {
        this.showPage(this.currentPage - 1);
      }
    });

    // Next button
    this.nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.currentPage < this.totalPages) {
        this.showPage(this.currentPage + 1);
      }
    });

    // Page number clicks (event delegation)
    this.pageNumbersContainer.addEventListener('click', (e) => {
      if (e.target.matches('[data-page]')) {
        e.preventDefault();
        const page = parseInt(e.target.dataset.page, 10);
        this.showPage(page);
      }
    });

    // Browser back/forward navigation
    window.addEventListener('popstate', (e) => {
      const page = this.getPageFromURL();
      this.currentPage = page;
      this.renderPageNumbers();
      this.showPage(page);
    });
  }
}

/**
 * Initialize all paginated lists on the page
 */
function initPaginatedLists() {
  const navElements = document.querySelectorAll('[data-paginated-list-nav]');
  navElements.forEach(nav => {
    new PaginatedList(nav);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPaginatedLists);
} else {
  initPaginatedLists();
}

// Re-initialize on Shopify theme editor section load
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-paginated-list-nav]')) {
    const nav = event.target.querySelector('[data-paginated-list-nav]');
    if (nav) {
      new PaginatedList(nav);
    }
  }
});
